import {
  css,
  DefaultTheme,
  FlattenInterpolation,
  Interpolation,
  ThemedStyledProps,
} from 'styled-components';
import { Breakpoint, TypographyVariant } from 'types';
import getLegacyThemeColor from './getLegacyThemeColor';
import getThemeColor, { getShorthandActionColor } from './getThemeColor';
import { ThemedBreakpointStyleProps } from './types';

export const getProp =
  <TProps, TName extends keyof TProps>(propName: TName) =>
  (props: ThemedStyledProps<TProps, DefaultTheme>): TProps[TName] =>
    props[propName];

export const getThemeFontFamily =
  (fontFamilyKey: keyof DefaultTheme['typography']['fontFamily']) =>
  (props: ThemedStyledProps<unknown, DefaultTheme>) =>
    props.theme.typography.fontFamily[fontFamilyKey];

export const getThemeTypography =
  (
    typographyKey: keyof DefaultTheme['typography']['variants'],
    subVariant: keyof TypographyVariant = 'default',
  ) =>
  (props: ThemedStyledProps<unknown, DefaultTheme>) => {
    const style = props.theme.typography.variants[typographyKey][subVariant];
    return !style ? undefined : css(style);
  };

export const createCSSInterpolation = <
  P extends object = Record<string, unknown>,
>(
  first: TemplateStringsArray,
  interpolations: Array<Interpolation<ThemedStyledProps<P, DefaultTheme>>>,
): FlattenInterpolation<ThemedStyledProps<P, DefaultTheme>> => {
  return css(first, ...interpolations);
};

export const breakpointDown =
  (size: Breakpoint) =>
  <P extends object = Record<string, unknown>>(
    first: TemplateStringsArray,
    ...interpolations: Array<Interpolation<ThemedStyledProps<P, DefaultTheme>>>
  ): FlattenInterpolation<ThemedStyledProps<P, DefaultTheme>> =>
    css`
      ${({ theme }) => theme.breakpoints.down(size)} {
        ${createCSSInterpolation(first, interpolations)}
      }
    `;

export const breakpointUp =
  (size: Breakpoint) =>
  <P extends object = Record<string, unknown>>(
    first: TemplateStringsArray,
    ...interpolations: Array<Interpolation<ThemedStyledProps<P, DefaultTheme>>>
  ): FlattenInterpolation<ThemedStyledProps<P, DefaultTheme>> =>
    css`
      ${({ theme }) => theme.breakpoints.up(size)} {
        ${createCSSInterpolation(first, interpolations)}
      }
    `;

export const themedBreakpointDown =
  <TProps extends ThemedBreakpointStyleProps>(
    first: TemplateStringsArray,
    ...interpolations: Array<
      Interpolation<ThemedStyledProps<TProps, DefaultTheme>>
    >
  ) =>
  (props: ThemedStyledProps<TProps, DefaultTheme>) =>
    breakpointDown(props.$breakpoint)(first, ...interpolations);

export { getLegacyThemeColor, getShorthandActionColor, getThemeColor };
export * from './types';
